import React, { useEffect, useState } from "react";
import { Pill, LiveDot, ButtonPrimary, ButtonGhost, Tick } from "./ui";

const HERO_LINES = [
  { role: "user",    text: "Collect overdue receivables from Tier-2 retailers in BD." },
  { role: "think",   text: "Pulling 312 invoices · 47 retailers · avg DPO 38d" },
  { role: "action",  text: "Segmenting by risk · ranking by recovery probability" },
  { role: "tool",    text: "tool.call(whatsapp.send) × 47 — personalized in Bangla / English" },
  { role: "result",  text: "21 retailers committed · 6 disputes routed · $284,200 expected this week" },
  { role: "learn",   text: "Updated policy: 'Friday 11am' lifts response rate by 18% — saved to memory" },
];

const TerminalRow = ({ line, idx }) => {
  const colors = {
    user:   { tag: "USER",    color: "text-white",          chip: "bg-white/10 text-white" },
    think:  { tag: "THINK",   color: "text-cyan-200",       chip: "bg-cyan-300/10 text-cyan-200" },
    action: { tag: "ACT",     color: "text-blue-200",       chip: "bg-blue-400/10 text-blue-200" },
    tool:   { tag: "TOOL",    color: "text-violet-200",     chip: "bg-violet-400/10 text-violet-200" },
    result: { tag: "RESULT",  color: "text-emerald-200",    chip: "bg-emerald-400/10 text-emerald-200" },
    learn:  { tag: "LEARN",   color: "text-amber-200",      chip: "bg-amber-300/10 text-amber-200" },
  };
  const c = colors[line.role];
  return (
    <div className="flex items-start gap-3 fadeup" style={{ animationDelay: `${idx * 80}ms` }}>
      <div className={`mt-[3px] shrink-0 rounded px-1.5 py-[2px] font-mono text-[9px] tracking-widest ${c.chip}`}>{c.tag}</div>
      <div className={`font-mono text-[12.5px] leading-[1.55] ${c.color}`}>{line.text}</div>
    </div>
  );
};

function AgentTerminal() {
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    if (step >= HERO_LINES.length) {
      const t = setTimeout(() => setStep(0), 2400);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setStep(s => s + 1), step === 0 ? 700 : 1100);
    return () => clearTimeout(t);
  }, [step, paused]);

  return (
    <div className="relative">
      <div aria-hidden="true" className="pointer-events-none absolute -inset-8 rounded-3xl opacity-60 blur-3xl" style={{ background: "radial-gradient(closest-side, rgba(94,234,212,0.18), transparent 70%)" }} />
      <div className="relative rounded-2xl border border-white/[0.08] bg-gradient-to-b from-[#0b1535] to-[#070d24] shadow-2xl shadow-black/40 overflow-hidden">
        <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-300/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
            <span className="ml-3 font-mono text-[11px] tracking-wider text-white/40">agent · ar_collector_bd · v2.7</span>
          </div>
          <div className="flex items-center gap-2 font-mono text-[10px] text-cyan-300/90">
            <LiveDot /><span>thinking</span>
          </div>
        </div>
        <div className="px-5 py-5 min-h-[330px] flex flex-col gap-3">
          {HERO_LINES.slice(0, step).map((line, i) => <TerminalRow key={i} line={line} idx={i} />)}
          {step < HERO_LINES.length && (
            <div className="flex items-center gap-2 font-mono text-[12px] text-white/40">
              <span className="caret">▍</span><span>agent reasoning…</span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-px bg-white/[0.06] border-t border-white/[0.06]">
          {[
            { k: "Recovered", v: "$284K", sub: "this week" },
            { k: "Auto-resolved", v: "91%", sub: "no human in loop" },
            { k: "Policies learned", v: "37", sub: "since launch" },
          ].map(s => (
            <div key={s.k} className="bg-[#070d24] px-4 py-3">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-white/40">{s.k}</div>
              <div className="mt-0.5 text-[18px] font-semibold nums text-white">{s.v}</div>
              <div className="font-mono text-[10px] text-white/40">{s.sub}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <button onClick={() => setPaused(p => !p)} className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white/80">
          {paused ? "▶ resume" : "⏸ pause"}
        </button>
      </div>
    </div>
  );
}

export default function Hero() {
  return (
    <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-50" />
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[1100px] rounded-full blur-[120px] opacity-40"
           style={{ background: "radial-gradient(closest-side, rgba(76,125,255,0.45), rgba(94,234,212,0.15), transparent 70%)" }} />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-[1.05fr,1fr] lg:px-10">
        <div>
          <Pill tone="accent">
            <LiveDot /> &nbsp;NEW · Self-learning Agent Engine v2.7
          </Pill>
          <h1 className="mt-6 text-[44px] leading-[1.02] tracking-tight sm:text-[56px] lg:text-[68px]">
            <span className="text-white">Custom AI agents that</span>{" "}
            <span className="font-display italic grad-text">teach themselves</span>{" "}
            <span className="text-white">to run your business.</span>
          </h1>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-white/70">
            EkkBaz AI engineers autonomous agents that perceive, decide, act and <span className="text-white">learn from every outcome</span> &mdash; taking over the operational work that drains your team, across sales, supply chain, customer experience, finance and ops.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <ButtonPrimary href="#book">Book a strategy call</ButtonPrimary>
            <ButtonGhost href="#demo"><LiveDot /> &nbsp;Watch an agent work</ButtonGhost>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12px] text-white/50">
            <span className="flex items-center gap-2"><Tick /> First agent live in 6 weeks</span>
            <span className="flex items-center gap-2"><Tick /> SOC 2 · ISO 27001 aligned</span>
            <span className="flex items-center gap-2"><Tick /> Private deployment available</span>
          </div>
        </div>
        <div className="lg:pl-4">
          <AgentTerminal />
        </div>
      </div>
    </section>
  );
}
